import { useEffect, useState } from 'react';

import { Button } from '../../components/Button';
import { SkeletonPulse } from '../../components/Skeleton';
import { CircleAlert, RotateCcw, X } from '../../icons';
import { View } from '../../primitives/View';
import { BRAND_HEX, roleColor } from '../../styles/brand-colors';
import { Circle, Svg } from '../../svg';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { AttachmentStatusAnnouncer } from './AttachmentStatusAnnouncer';
import { AttachmentVeilFade } from './AttachmentVeilFade';
import {
  CHAT_ATTACHMENT_FADE_MS,
  CHAT_ATTACHMENT_RING,
  chatAttachmentRingGeometry,
  type ChatAttachmentTileState,
  chatAttachmentTileState,
} from './chat-attachment-tile';
import type { ChatAttachmentTileProps } from './ChatAttachmentTile.types';

/**
 * The progress ring's stroke over the upload scrim. The `scrim` role is STABLE
 * (secondary-950 in both themes), so what sits on it is stable too — the light
 * end of the same ramp, read from the palette rather than a literal.
 */
const ON_SCRIM = BRAND_HEX.secondary['50'];

type VeilOverlay = Exclude<ChatAttachmentTileState['overlay'], 'none'>;

/**
 * One image attached in the chat composer, as both clients show it — the
 * pattern the big chat apps (and their open-source clones) converged on: the
 * LOCAL preview appears at once at its final 56px size; while it uploads a
 * stable scrim dims it (with a 2px blur on web), breathing on the shared
 * skeleton pulse — or, when the transport reports a fraction, holding still
 * under a 20px determinate ring. When the upload lands the whole veil fades
 * out in 200ms (opacity only) and the preview is crisp; nothing moves. A
 * failed upload washes out under a calm surface veil, takes the alert border
 * and offers a DS ghost retry. The remove X — a DS secondary icon button on the
 * corner — is always there, in every state.
 *
 * The preview itself is a slot — `<img>` of an object URL on web, RN `Image`
 * of the asset uri on mobile. Labels arrive pre-translated.
 */
export function ChatAttachmentTile({
  status,
  progress,
  children,
  onRemove,
  onRetry,
  uploadingLabel,
  failedLabel,
  removeLabel,
  retryLabel,
  className,
  testID,
}: ChatAttachmentTileProps) {
  const state = chatAttachmentTileState(status, progress);
  const { isDark } = useTheme();

  // While the veil fades out it keeps painting what it last showed (the pulse
  // keeps breathing for those 200ms rather than snapping to another shade),
  // then unmounts so no animation runs under a finished tile.
  // (State adjusted during render, not in an effect — no stale frame between.)
  const [overlay, setOverlay] = useState<VeilOverlay>(
    state.overlay === 'none' ? 'pulse' : state.overlay,
  );
  if (state.overlay !== 'none' && state.overlay !== overlay) setOverlay(state.overlay);
  const [lingering, setLingering] = useState(state.veiled);
  if (state.veiled && !lingering) setLingering(true);
  useEffect(() => {
    if (state.veiled) return;
    const id = setTimeout(() => setLingering(false), CHAT_ATTACHMENT_FADE_MS + 50);
    return () => clearTimeout(id);
  }, [state.veiled]);
  const veilMounted = state.veiled || lingering;

  const announce =
    status === 'uploading' ? uploadingLabel : status === 'error' ? failedLabel : null;

  return (
    // The outer box is the 56px cell; the FRAME inside it clips the preview,
    // and the remove button sits on the outer box so it can straddle the
    // corner instead of crowding the image.
    <View testID={testID} className={cn('relative h-14 w-14', className)}>
      <View
        className={cn(
          'absolute inset-0 overflow-hidden rounded-xl border bg-secondary-100',
          status === 'error' ? 'border-alert-500' : 'border-secondary-200',
        )}
        webProps={{ title: status === 'error' ? failedLabel : undefined }}>
        {children}

        {veilMounted && (
          <AttachmentVeilFade visible={state.veiled}>
            <View className="absolute inset-0 web:backdrop-blur-[2px]" />
            {overlay === 'pulse' && (
              <SkeletonPulse key="pulse" className="absolute inset-0">
                <View className="h-full w-full bg-scrim/40" />
              </SkeletonPulse>
            )}
            {overlay === 'ring' && (
              <View key="ring" className="absolute inset-0 items-center justify-center">
                <View className="absolute inset-0 bg-scrim/30" />
                {/* `relative`: on web an un-positioned child paints UNDER the
                    absolute scrim beside it. */}
                <View className="relative">
                  <ProgressRing fraction={state.fraction} />
                </View>
              </View>
            )}
            {overlay === 'alert' && (
              // Failed: a calm SURFACE veil (secondary-50, mirrored in dark)
              // washes the picture out instead of a muddy dark scrim, so the
              // alert border carries the state and the ghost retry sits on it
              // like on any surface.
              <View key="alert" className="absolute inset-0 items-center justify-center">
                <View className="absolute inset-0 bg-secondary-50/80" />
                <View className="relative">
                  {onRetry ? (
                    <Button
                      variant="ghost"
                      size="xxs"
                      icon={RotateCcw}
                      accessibilityLabel={retryLabel ?? failedLabel}
                      testID={subTestID(testID, 'retry')}
                      onPress={onRetry}
                      webProps={{ type: 'button', title: retryLabel }}
                      nativeProps={{ hitSlop: 8 }}
                    />
                  ) : (
                    <CircleAlert
                      size={16}
                      color={roleColor('fill-alert', isDark)}
                      strokeWidth={2.5}
                    />
                  )}
                </View>
              </View>
            )}
          </AttachmentVeilFade>
        )}
      </View>

      {/* Remove: the DS secondary icon button (a solid chip with its own
          border, legible on any picture, light or dark), straddling the
          corner by the 8px grid step. Live in every state. */}
      <View className="absolute -right-2 -top-2">
        <Button
          variant="secondary"
          size="xxs"
          icon={X}
          accessibilityLabel={removeLabel}
          testID={subTestID(testID, 'remove')}
          onPress={onRemove}
          webProps={{ type: 'button', title: removeLabel }}
          nativeProps={{ hitSlop: 8 }}
        />
      </View>

      <AttachmentStatusAnnouncer label={announce} />
    </View>
  );
}

function ProgressRing({ fraction }: { fraction: number }) {
  const { size, stroke } = CHAT_ATTACHMENT_RING;
  const { radius, circumference, dashOffset } = chatAttachmentRingGeometry(fraction);
  const c = size / 2;
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <Circle
        cx={c}
        cy={c}
        r={radius}
        stroke={ON_SCRIM}
        strokeOpacity={0.35}
        strokeWidth={stroke}
        fill="none"
      />
      <Circle
        cx={c}
        cy={c}
        r={radius}
        stroke={ON_SCRIM}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={dashOffset}
        // Start at 12 o'clock instead of 3.
        transform={`rotate(-90 ${c} ${c})`}
        fill="none"
      />
    </Svg>
  );
}
